<template>
  <v-card class="mx-n4 mb-12" elevation="0">
    <v-dialog v-model="paymentDialog" width="875" persistent>
      <v-card color="white">
        <iframe
          width="850"
          height="600"
          :src="iframeSrc"
          frameborder="0"
          sandbox="allow-forms allow-popups allow-pointer-lock allow-same-origin allow-scripts"
        ></iframe>
      </v-card>
    </v-dialog>
    <v-snackbar
      top
      color="error"
      v-model="snackbar"
      :timeout="6000"
      style="z-index: 9999"
    >
      {{ $t("payment.error") }}
      <v-btn dark text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
    <v-card-title>{{ $t("form.labels.payment") }}</v-card-title>
    <v-card-text>
      <v-tabs v-model="tab" grow>
        <v-tab>
          <v-icon class="mr-2">mdi-credit-card</v-icon>
          {{ $t("form.labels.creditcard") }}
        </v-tab>
        <v-tab>
          <v-icon class="mr-2">mdi-bank</v-icon>
          {{ $t("form.labels.sofort") }}
        </v-tab>
        <v-tab>
          <v-icon class="mr-2">mdi-account-cash</v-icon>
          {{ $t("form.labels.banktransfer") }}
        </v-tab>

        <v-tab-item>
          <v-card flat class="mt-4">
            <v-form>
              <v-stripe-card
                outlined
                :api-key="apiKey"
                ref="stripeCard"
                v-model="token"
                :zip="user.zip"
                :label="$t('form.labels.creditcard')"
              ></v-stripe-card>
            </v-form>
          </v-card>
        </v-tab-item>
        <v-tab-item>
          <v-card flat class="mt-4"> </v-card>
        </v-tab-item>

        <v-tab-item>
          <v-card flat class="mt-4">
            <v-card-title>{{ $t("form.bankTransfer.headline") }}</v-card-title>
            <v-card-text>
              <span class="subtitle-2">{{ $t("form.bankTransfer.text") }}</span>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs>
      <v-checkbox outlined v-model="agbAccepted">
        <template v-slot:label>
          <span v-html="$t('form.fields.agb') + ' *'"></span>
        </template>
      </v-checkbox>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        class="mr-4"
        @click="submit"
        :loading="loading"
        :disabled="!agbAccepted"
      >
        {{ $t("form.btn.pay") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters, mapActions, mapMutations, mapState } from "vuex";
export default {
  data() {
    return {
      stripe: null,
      stripElements: null,
      cardPayment: null,
      token: null,
      tab: null,
      agbAccepted: false,
      snackbar: null,
      paymentDialog: null,
      iframeSrc: null,
      payment: null,
      pendingPaymentAmount: 0,
      timer: null,
      pageType: "OEGG",
    };
  },

  mounted() {
    const queryParams = new URLSearchParams(window.location.search);
    const paymentIntentClientSecret = queryParams.get(
      "payment_intent_client_secret"
    );

    if (paymentIntentClientSecret) {
      this.toggleLoading(true);
      this.checkStripeCard(paymentIntentClientSecret);
    }
  },

  methods: {
    ...mapActions([
      "authorize",
      "authorizeBankTransfer",
      "toggleLoading",
      "charge",
      "submitReservation",
    ]),
    ...mapMutations(["setPayment"]),

    async checkStripeCard(paymentIntentClientSecret) {
      if (this.$refs.stripeCard && this.$refs.stripeCard.stripe) {
        try {
          const stripe = this.$refs.stripeCard.stripe;
          const { paymentIntent } = await stripe.retrievePaymentIntent(
            paymentIntentClientSecret
          );
          if (paymentIntent.status === "succeeded") {
            const url = new URL(window.location.href);
            url.search = "";
            window.history.pushState({}, "", url.href);
            this.setPayment({
              id: paymentIntent.id,
              method: "klarna",
            });
            await this.submitReservation();
            this.$emit("onContinue");
          } else {
            console.log("PaymentIntent status: ", paymentIntent.status);
            this.snackbar = true;
          }
        } catch (err) {
          console.log("error ", err);
          this.snackbar = true;
        } finally {
          this.toggleLoading(false);
        }
      } else {
        setTimeout(() => this.checkStripeCard(paymentIntentClientSecret), 100); // Check again after 100ms
      }
    },
    async submit() {
      this.toggleLoading(true);

      try {
        if (this.tab === 0) {
          await this.submitCardPayment();
        }
        if (this.tab === 1) {
          await this.submitSofortPayment();
        }
        if (this.tab === 2) {
          await this.authorizeBankTransfer();
        }

        await this.submitReservation();
        this.$emit("onContinue");
      } catch (err) {
        this.snackbar = true;
      } finally {
        this.toggleLoading(false);
      }
    },

    async submitCardPayment() {
      const paymentIntent = await this.authorize();
      const stripe = this.$refs.stripeCard.stripe;
      const res = await stripe.confirmCardPayment(
        paymentIntent.result.client_secret,
        {
          payment_method: {
            card: this.$refs.stripeCard.card,
            billing_details: {
              name: this.user.firstName + " " + this.user.lastName,
              email: this.user.email,
            },
          },
          save_payment_method: false,
        }
      );

      if (res.paymentIntent.status === "succeeded") {
        this.setPayment({
          id: res.paymentIntent.id,
          method: "card",
        });
      }
    },

    async submitSofortPayment() {
      const paymentIntent = await this.authorize();
      const stripe = this.$refs.stripeCard.stripe;

      // remove all previous query parameters
      const url = new URL(window.location.href);
      url.search = "";
      window.history.pushState({}, "", url.href);
      await stripe.confirmKlarnaPayment(paymentIntent.result.client_secret, {
        payment_method: {
          billing_details: {
            name: this.user.firstName + " " + this.user.lastName,
            email: this.user.email,
            address: {
              country: this.user.country,
            },
          },
        },
        return_url: window.location.href,
        save_payment_method: false,
      });
    },
  },

  computed: {
    ...mapGetters(["cartSum"]),
    ...mapState(["user", "loading"]),
    apiKey() {
      return process.env.VUE_APP_STRIPE_PUBLIC_KEY_OEGG;
      return (
        window.stripePublicKey ||
        process.env["VUE_APP_STRIPE_PUBLIC_KEY_" + this.pageType]
      );
    },
  },
};
</script>

<style></style>
